import React, { useMemo } from 'react'
import { withStyles } from '@mui/styles'
import { TextField, FormControl, FormHelperText, Tooltip } from '@mui/material'

import { Icon, Typography, MenuItem } from '@mui/material'

import { textInputStyles } from './text-input.styles'

const ROWS_MAX = 5
const DEFAULT_MAX_LENGTH = 250

/**
 * @typedef {object} TextInputChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop name.
 * @property {string} target.value - El nuevo valor del campo de texto.
 */

/**
 * @typedef {object} TextInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {'text' | 'email' | 'password' | 'number'} [type='text'] - El tipo de input HTML.
 * @property {boolean} [readOnly=false] - Si es true, el campo se muestra como texto de solo lectura.
 * @property {boolean} [required=false] - Indica si el campo es obligatorio.
 * @property {number} [rowsMax=5] - Número máximo de filas para campos multilínea.
 * @property {number} [rows=1] - Número de filas iniciales para campos multilínea.
 * @property {string} [value] - El valor actual del campo de texto.
 * @property {boolean} [hidden] - Si es true, el componente no renderiza nada.
 * @property {string} [title] - El título o etiqueta que se muestra sobre el campo.
 * @property {string} [name] - El nombre del campo, que se incluirá en el objeto de evento del onChange.
 * @property {string} [placeholder] - Texto de marcador de posición que aparece cuando el campo está vacío.
 * @property {string} [tooltip] - Texto informativo que se muestra en un icono de ayuda junto al título.
 * @property {string} [tooltipValue] - Texto informativo que se muestra en modo readOnly.
 * @property {string} [units] - Unidades que se muestran después del valor en modo readOnly.
 * @property {string} [helpMessage] - Mensaje de ayuda que se muestra debajo del campo.
 * @property {boolean} [error] - Si es true, el campo se muestra en estado de error.
 * @property {string} [errorMessage] - Mensaje de error que se muestra cuando error es true.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título y valor readOnly.
 * @property {Array<{key: string, value: string}>} [values] - Array de opciones para convertir el campo en select.
 * @property {object} [InputProps] - Props adicionales para el componente TextField de Material-UI.
 * @property {object} [inputProps] - Props adicionales para el elemento input HTML nativo.
 * @property {function(TextInputChangeEvent): void} onChange - La función callback que se ejecuta cuando cambia el valor.
 */

/**
 * Un componente de entrada de texto versátil que puede funcionar como campo de texto simple,
 * área de texto multilínea, o select con opciones.
 *
 * Soporta modo de solo lectura, validación, tooltips informativos, y personalización de estilo.
 * Puede mostrar mensajes de error y ayuda, y adapta su apariencia según el estado y configuración.
 *
 * @component
 * @example
 * const MyForm = () => {
 *   const [email, setEmail] = useState('');
 *
 *   const handleChange = (e) => {
 *     console.log(`El campo '${e.target.name}' ahora es: ${e.target.value}`);
 *     setEmail(e.target.value);
 *   };
 *
 *   return (
 *     <TextInput
 *       title="Correo Electrónico"
 *       name="email"
 *       type="email"
 *       value={email}
 *       onChange={handleChange}
 *       required
 *       placeholder="ejemplo@correo.com"
 *       tooltip="Ingrese un correo válido"
 *     />
 *   );
 * };
 *
 * @param {TextInputProps} props - Las props del componente TextInput.
 */
export const TextInput = React.memo(
  withStyles(textInputStyles)(
    ({ classes, type = 'text', readOnly = false, rowsMax = ROWS_MAX, rows = 1, ...props }) => {
      const { error, errorMessage, hidden, ...restProps } = props
      if (hidden) return null
      const InputProps = { className: classes.input, ...props.InputProps }
      const tooltip = props.tooltip ? (
        <span style={{ fontSize: 16, marginLeft: 4 }}>
          <Tooltip
            placement="top"
            title={
              <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                {props.tooltip}
              </Typography>
            }
          >
            <Icon fontSize="inherit">help_outline</Icon>
          </Tooltip>
        </span>
      ) : null
      const inputPropsDefault = {
        pattern: props.required ? '.*[^ ].*' : null,
        maxLength: DEFAULT_MAX_LENGTH,
      }

      const typographyVariant = props.fontSize === 'small' ? '2' : '1'
      return (
        <FormControl className={classes.root}>
          {props.title ? (
            <Typography variant={`subtitle${typographyVariant}`}>
              {props.title}
              {tooltip ? tooltip : ''}
              {props.required ? <span style={{ color: '#B3272C' }}> *</span> : null}
            </Typography>
          ) : null}

          {readOnly ? (
            <div style={{ display: 'flex' }}>
              <Typography
                variant={`body${typographyVariant}`}
                color="textSecondary"
                style={{ whiteSpace: 'pre-line' }}
              >
                {props.value ? props.value + (props.units ? ' ' + props.units : '') : '--'}
              </Typography>
              {props.tooltipValue ? (
                <span style={{ fontSize: 16, marginLeft: 4 }}>
                  <Tooltip
                    placement="top"
                    title={
                      <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                        {props.tooltipValue}
                      </Typography>
                    }
                  >
                    <Icon fontSize="inherit">help_outline</Icon>
                  </Tooltip>
                </span>
              ) : null}
            </div>
          ) : (
            <>
              <TextField
                maxRows={rowsMax}
                minRows={rows}
                {...restProps}
                type={type}
                variant="outlined"
                value={props.value || ''}
                placeholder={props.placeholder}
                onChange={props.onChange}
                InputProps={InputProps}
                inputProps={{ ...inputPropsDefault, ...props.inputProps }}
              >
                EMPRESA_COMERCIALIZADORA
                {props.values
                  ? props.values.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.value}
                      </MenuItem>
                    ))
                  : null}
              </TextField>
              {(error && errorMessage) || props.helpMessage ? (
                <FormHelperText id="my-helper-text" error={error}>
                  {error ? errorMessage : props.helpMessage}
                </FormHelperText>
              ) : null}
            </>
          )}
        </FormControl>
      )
    }
  )
)
